import { Record, List, Map } from "immutable";
import moment from "moment";
import { formatDateSmart } from "~/utils/date.jsx";
import Project from "./record.project.jsx";
import User from "./record.user.jsx";

const TaskRecord = Record({
  id: undefined,
  assignee_id: undefined,
  assignee_ids: undefined,
  attachments: List(),
  assignees: List(),
  body: ``,
  body_formatted: ``,
  body_mode: `paragraph`,
  class: `Task`,
  comments_count: 0,
  completed_by_id: undefined,
  completed_by_name: ``,
  completed_on: undefined,
  completed_subtasks: 0,
  created_by_id: undefined,
  created_from_recurring_task_id: undefined,
  created_on: undefined,
  delegated_by_id: undefined,
  due_on: undefined,
  estimate: ``,
  tracked_time: 0,
  tracked_expenses: 0,
  is_completed: false,
  is_hidden_from_clients: false,
  is_billable: true,
  is_important: false,
  is_trashed: false,
  original_is_trashed: false,
  job_type_id: undefined,
  labels: List(),
  subtasks: List(),
  name: ``,
  open_subtasks: 0,
  position: null,
  project_id: undefined,
  start_on: undefined,
  task_list_id: undefined,
  task_number: undefined,
  total_subtasks: 0,
  trashed_by_id: undefined,
  trashed_on: undefined,
  updated_by_id: undefined,
  updated_on: undefined,
  url_path: ``,
  _fetchingComments: false,
  _pending: false,
  fake_assignee_email: undefined,
  fake_assignee_name: undefined,
  open_dependencies: undefined,
  created_by_name: ``,
  created_by_email: ``,
  estimate_by_date: Map({}),
  daily_estimate: 0,
  is_split: undefined,
});

class Task extends TaskRecord {
  isFetchingComments() {
    return !!this._fetchingComments;
  }

  getStartOn() {
    return moment.utc(this.start_on * 1000);
  }

  getDueOn() {
    return moment.utc(this.due_on * 1000);
  }

  getDueIndicator() {
    if (!this.due_on) return `not_set`;

    let due_on = moment.utc(this.due_on * 1000).startOf(`day`);
    let now = moment().startOf(`day`);

    // today
    if (due_on.format(`YYYY-MM-DD`) === now.format(`YYYY-MM-DD`)) {
      return `today`;
    }

    // overdue
    if (!due_on.isAfter(now)) {
      return `overdue`;
    }

    // upcoming
    if (moment(due_on).isBefore(now.add(7, `days`))) {
      return `upcoming`;
    }

    // schedule in future
    return `scheduled`;
  }

  isOverdue() {
    return this.getDueIndicator() === `overdue`;
  }

  isToday() {
    return this.getDueIndicator() === `today`;
  }

  isUpcoming() {
    return this.getDueIndicator() === `upcoming`;
  }

  isScheduled() {
    return this.getDueIndicator() === `scheduled`;
  }

  isFromRecurring() {
    return !!this.created_from_recurring_task_id && this.created_from_recurring_task_id > 0;
  }

  canSee(user, project) {
    if (user instanceof User) {
      if (user.isOwner()) return true;

      if (project instanceof Project && this.project_id === project.id) {
        if (project.isLeader(user)) return true;
        if (project.isMember(user)) {
          return !(user.isClient() && this.is_hidden_from_clients);
        }
      }
    }

    return false;
  }

  canEdit(user, project) {
    if (!this.canSee(user, project)) return false;

    return user.isOwner() || project.isLeader(user) || user.isMember() || user.isClientPlus();
  }

  canDelete(user, project) {
    return this.canEdit(user, project);
  }

  getPrintDates(dateFormat) {
    if (this.due_on && this.start_on && this.due_on !== this.start_on) {
      return `${formatDateSmart(this.start_on, dateFormat)} - ${formatDateSmart(this.due_on, dateFormat)}`;
    }

    if (this.due_on && (!this.start_on || this.due_on === this.start_on)) {
      return `${formatDateSmart(this.due_on, dateFormat)}`;
    }

    return ``;
  }

  getHashUrl() {
    return `Task-${this.id}-${this.project_id}`;
  }

  getViewUrl() {
    if (this.url_path && this.url_path[0] === "/") {
      return this.url_path.substring(1);
    }
    return this.url_path;
  }
}

export default Task;
